import { useEffect, useMemo } from 'react';
import { useTable, useSortBy, useExpanded } from 'react-table';
import ThemeLink from '../../components/ThemeLink';
import AsyncFiltersDisplay from '../../components/AsyncFiltersDisplay';
import { FaArrowDown, FaArrowUp } from 'react-icons/fa';
import { IoIosCheckmarkCircleOutline } from 'react-icons/io';
import { MdOutlineErrorOutline } from 'react-icons/md';

import { CgSpinner } from 'react-icons/cg';

import { formatDateCellShort } from '../../helpers/reactTable';
import { useQuery } from '@apollo/client';
import { ACTIVATIONS_QUERY } from '../../queries/activationQueries';
import LoadingSpinner from '../../components/LoadingSpinner';
import ErrorText from '../../components/ErrorText';

export default function Distributions() {
  const { loading, error, data, stopPolling, startPolling } = useQuery(
    ACTIVATIONS_QUERY,
    {
      fetchPolicy: 'cache-and-network',
    }
  );

  useEffect(() => {
    if (
      data?.activations.every(
        (act) =>
          act.processingStatus === 'completed' ||
          act.processingStatus === 'failed'
      )
    ) {
      stopPolling();
    } else {
      startPolling(1000 * 5); // 5 seconds
    }

    return () => stopPolling();
  }, [data, stopPolling, startPolling]);

  return (
    <div className="w-full">
      {loading && (
        <LoadingSpinner
          message="Getting your distributions..."
          className="mt-10"
        />
      )}
      {!loading && error && <ErrorText message={error.message} />}
      {!loading && !error && data && (
        <DistributionsTable data={data.activations} />
      )}
    </div>
  );
}

function DistributionsTable({ data }) {
  const columns = useMemo(
    () => [
      {
        Header: 'Status',
        accessor: 'processingStatus',
        Cell: ({ value }) => {
          if (value == 'pending') {
            return (
              <span className="flex items-center gap-1">
                <CgSpinner className="text-2xl text-blue animate-spin" />
                <span>Processing</span>
              </span>
            );
          }

          if (value == 'failed') {
            return (
              <span className="flex items-center gap-1">
                <MdOutlineErrorOutline className="text-2xl text-orange" />
                <span>Failed</span>
              </span>
            );
          }

          return (
            <span className="flex items-center gap-1">
              <IoIosCheckmarkCircleOutline className="text-2xl text-green-light" />
              <span>Completed</span>
            </span>
          );
        },
      },
      {
        // ATAD-132: fall back to the names denormalized onto the activation.
        // A deleted audience or a missing destination ref used to render an
        // empty cell; the row can still say what it was.
        Header: 'Audience Name',
        id: 'audienceName',
        accessor: (row) => row.audience?.name || row.audienceName || '—',
      },
      {
        Header: 'Destination Partner',
        id: 'destinationPartnerName',
        accessor: (row) =>
          row.destinationPartner?.name || row.destinationPartnerName || '—',
      },
      {
        // For LiveRamp variants this surfaces the folder name (same value
        // we render in place of "Connected" on the Activations builder).
        // For non-LiveRamp partners the field is null on the document, so
        // we fall back to an em-dash.
        Header: 'LiveRamp Folder',
        id: 'liveRampFolder',
        accessor: 'destinationPartner.displayName',
        Cell: ({ value }) => value || '—',
      },
      {
        Header: 'Destination Ad Account ID',
        id: 'destinationPartnerId',
        accessor: (row) => row.destinationPartner?.id || '—',
      },
      {
        Header: 'Audience Size',
        accessor: 'recordsIncluded',
        Cell: ({ value }) => (value as number).toLocaleString('en-US'),
      },
      {
        Header: 'Date Sent',
        accessor: 'startedAt',
        Cell: ({ value }) => formatDateCellShort({ value: Number(value) }),
      },
      {
        Header: 'Details',
        accessor: 'id',
        id: 'details',
        Cell: ({ value }) => {
          return (
            <ThemeLink to={`/dashboard/activations/summary/${value}`}>
              View
            </ThemeLink>
          );
        },
      },
    ],
    []
  );

  const initialSortBy = useMemo(() => [{ id: 'dateSent', desc: true }], []);

  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: {
        sortBy: initialSortBy,
      },
    },
    useSortBy,
    useExpanded
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    visibleColumns,
  } = tableInstance;

  return (
    <div className="h-full w-full overflow-auto pb-6">
      <table {...getTableProps()} className="w-full">
        <thead className="whitespace-nowrap">
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  className="border-b-2 border-black p-2 hover:bg-gray-100"
                >
                  <div className="flex items-center">
                    {column.render('Header')}
                    {column.isSorted && (
                      <span className="pl-2">
                        {column.isSortedDesc ? <FaArrowDown /> : <FaArrowUp />}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()} className="whitespace-nowrap">
          {rows.map((row) => {
            prepareRow(row);
            return (
              <>
                <tr {...row.getRowProps()}>
                  {row.cells.map((cell) => (
                    <td className="p-2" {...cell.getCellProps()}>
                      {cell.render('Cell')}
                    </td>
                  ))}
                </tr>
                {row.isExpanded && (
                  <tr>
                    <td colSpan={visibleColumns.length}>
                      <div className="pt-2 px-14 pb-6 border-t w-full">
                        <AsyncFiltersDisplay audienceName={row.original.name} />
                      </div>
                    </td>
                  </tr>
                )}
              </>
            );
          })}
        </tbody>
      </table>
      {rows.length === 0 && (
        <p className="text-center pt-6">
          You don't have any distributions yet. You can activate an audience in
          the{' '}
          <ThemeLink to="/dashboard/activations/activationbuilder">
            activations builder.
          </ThemeLink>
        </p>
      )}
    </div>
  );
}
